import { useState } from 'react'
import { ROLES, type Role } from '@pharmacia/core'
import { ApiError, del, patch, post } from '../lib/api.ts'
import { useList } from '../lib/useList.ts'
import { relative } from '../lib/format.ts'
import { useAuth } from '../lib/auth.tsx'
import { PageHeader } from '../components/AppShell.tsx'
import { Modal } from '../components/Modal.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'
import { useToast } from '../components/Toast.tsx'
import { Button, Empty, Field, Input, RoleBadge, Select, SkeletonRows } from '../components/ui.tsx'
import { IconPlus, IconSearch } from '../components/icons.tsx'

type StaffRow = {
  id: string; email: string; fullName: string; role: Role
  isActive: boolean; version: number; createdAt: string; updatedAt: string
}

const ROLE_HELP: Record<Role, string> = {
  owner: 'Full access. Manages staff and reads the audit log.',
  doctor: 'Patient records only.',
  reception: 'Patient records only.',
}

export function StaffPage() {
  const { me } = useAuth()
  const toast = useToast()
  const list = useList<StaffRow>('/api/staff')
  const [inviting, setInviting] = useState(false)
  const [editing, setEditing] = useState<StaffRow | null>(null)
  const [removing, setRemoving] = useState<StaffRow | null>(null)

  // The API is the authority on all of this; these are the same rules
  // restated so the UI does not offer an action that will be refused.
  const isSelf = (s: StaffRow) => s.id === me?.staff.id

  return (
    <>
      <PageHeader title="Staff">
        <Button variant="primary" onClick={() => setInviting(true)}>
          <IconPlus /> Add staff member
        </Button>
      </PageHeader>

      <div className="content">
        <div className="page-max stack" style={{ gap: 'var(--s-5)' }}>
          <div className="toolbar">
            <div className="search">
              <IconSearch className="ic" />
              <Input
                placeholder="Search by name or email"
                value={list.q}
                onChange={e => list.setQ(e.target.value)}
                aria-label="Search staff"
              />
            </div>
            <span className="dim" style={{ fontSize: 'var(--text-sm)' }}>
              {list.loading ? '' : `${list.items.length}${list.hasMore ? '+' : ''} shown`}
            </span>
          </div>

          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Name</th><th>Email</th><th>Role</th><th>Status</th>
                    <th>Updated</th><th />
                  </tr>
                </thead>
                <tbody>
                  {list.loading ? <SkeletonRows rows={5} cols={6} /> : null}

                  {!list.loading && list.items.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 550 }}>
                        {s.fullName}
                        {isSelf(s) ? <span className="dim" style={{ fontWeight: 400 }}> · you</span> : null}
                      </td>
                      <td className="muted">{s.email}</td>
                      <td><RoleBadge role={s.role} /></td>
                      <td>
                        {s.isActive
                          ? <span className="badge doctor"><i className="dot" />Active</span>
                          : <span className="badge off"><i className="dot" />Deactivated</span>}
                      </td>
                      <td className="dim">{relative(s.updatedAt)}</td>
                      <td className="actions">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(s)}>Edit</Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => setRemoving(s)}
                          disabled={isSelf(s)}
                          title={isSelf(s) ? 'You cannot remove your own account' : undefined}
                          style={{ color: isSelf(s) ? undefined : 'var(--danger)' }}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!list.loading && list.items.length === 0 ? (
              <Empty title={list.q ? 'No matches' : 'No staff yet'}>
                {list.q ? 'Try a different name or email.' : 'Add the first staff member to get started.'}
              </Empty>
            ) : null}

            {list.error ? (
              <div className="empty">
                <p style={{ color: 'var(--danger)' }}>{list.error}</p>
                <Button onClick={list.refresh} style={{ marginTop: 'var(--s-3)' }}>Try again</Button>
              </div>
            ) : null}
          </div>

          {list.hasMore ? (
            <div className="row" style={{ justifyContent: 'center' }}>
              <Button onClick={list.loadMore} loading={list.loadingMore}>Load more</Button>
            </div>
          ) : null}
        </div>
      </div>

      {inviting ? (
        <StaffForm
          onClose={() => setInviting(false)}
          onSaved={() => { setInviting(false); list.refresh(); toast.ok('Staff member added') }}
        />
      ) : null}

      {editing ? (
        <StaffForm
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); list.refresh(); toast.ok('Changes saved') }}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          title={`Remove ${removing.fullName}?`}
          body={'They lose access immediately, and any open session is ended. The record is kept, not erased, because the audit log and patient records still point at it.'}
          confirmLabel="Remove"
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await del(`/api/staff/${removing.id}`)
              list.refresh()
              toast.ok('Staff member removed')
            } catch (e) {
              const err = e as ApiError
              toast.fail(
                err.code === 'LAST_OWNER' ? 'Cannot remove the last owner' : 'Could not remove',
                err.message,
              )
            }
          }}
        />
      ) : null}
    </>
  )
}

// ------------------------------------------------------------ create / edit
function StaffForm({ existing, onClose, onSaved }: {
  existing?: StaffRow; onClose: () => void; onSaved: () => void
}) {
  const toast = useToast()
  const editMode = Boolean(existing)
  const [fullName, setFullName] = useState(existing?.fullName ?? '')
  const [email, setEmail] = useState(existing?.email ?? '')
  const [role, setRole] = useState<Role>(existing?.role ?? 'reception')
  const [isActive, setIsActive] = useState(existing?.isActive ?? true)
  const [busy, setBusy] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setFieldErrors({})
    try {
      if (existing) {
        await patch(`/api/staff/${existing.id}`, {
          fullName, role, isActive,
          // The version we loaded. If somebody else saved in the meantime
          // the API refuses rather than overwriting their change.
          version: existing.version,
        })
      } else {
        await post('/api/staff', {
          // Generated here so a retry after a lost response replays the
          // same id and cannot create a second staff member.
          id: crypto.randomUUID(),
          email, fullName, role,
        })
      }
      onSaved()
    } catch (e) {
      const err = e as ApiError
      if (err.code === 'VALIDATION_FAILED') setFieldErrors(err.fieldErrors)
      else if (err.code === 'STALE_WRITE') toast.fail('Someone else saved first', err.message)
      else if (err.code === 'LAST_OWNER') toast.fail('Cannot change the last owner', err.message)
      else if (err.code === 'CONFLICT') setFieldErrors({ email: err.message })
      else toast.fail(editMode ? 'Could not save' : 'Could not add', err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={editMode ? `Edit ${existing!.fullName}` : 'Add staff member'}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Button type="button" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" form="staff-form" variant="primary" loading={busy}>
            {editMode ? 'Save changes' : 'Add staff member'}
          </Button>
        </>
      }
    >
      <form id="staff-form" onSubmit={submit} className="stack" style={{ gap: 'var(--s-4)' }}>
        <Field label="Full name" htmlFor="sf-name" error={fieldErrors['fullName']}>
          <Input
            id="sf-name" value={fullName} required maxLength={120}
            onChange={e => setFullName(e.target.value)}
            aria-invalid={Boolean(fieldErrors['fullName'])}
          />
        </Field>

        <Field
          label="Google account"
          htmlFor="sf-email"
          error={fieldErrors['email']}
          hint={editMode
            ? 'The sign-in address cannot be changed. Remove and re-add instead.'
            : 'This exact Google account is the only one that will be let in.'}
        >
          <Input
            id="sf-email" type="email" value={email} required maxLength={254}
            disabled={editMode} autoComplete="off"
            onChange={e => setEmail(e.target.value)}
            aria-invalid={Boolean(fieldErrors['email'])}
          />
        </Field>

        <Field label="Role" htmlFor="sf-role" hint={ROLE_HELP[role]} error={fieldErrors['role']}>
          <Select id="sf-role" value={role} onChange={e => setRole(e.target.value as Role)}>
            {ROLES.map(r => (
              <option key={r} value={r}>{r[0]!.toUpperCase() + r.slice(1)}</option>
            ))}
          </Select>
        </Field>

        {editMode ? (
          <Field
            label="Access"
            htmlFor="sf-active"
            hint={isActive
              ? 'Can sign in and use the portal.'
              : 'Signed out everywhere immediately and cannot sign back in.'}
          >
            <Select
              id="sf-active" value={isActive ? 'active' : 'off'}
              onChange={e => setIsActive(e.target.value === 'active')}
            >
              <option value="active">Active</option>
              <option value="off">Deactivated</option>
            </Select>
          </Field>
        ) : null}
      </form>
    </Modal>
  )
}
